/**
 * CrmCalendario — Monthly calendar view of reservas + eventos for the CRM.
 *
 * UX:
 *  - Header with the current month label and prev / today / next controls.
 *  - 7-column grid (Lun-Dom, ISO-style week start on Monday) with leading/trailing
 *    days from the adjacent months greyed out so the grid is always 6 rows.
 *  - Each cell shows up to 3 chips: gold = reservas, oxblood = eventos. Extra items
 *    are summarised as "+N más". Cells with content can be opened in a side modal.
 *  - The side modal lists everything for the selected day with quick links to the
 *    detail pages (eventos) and inline status pills for reservas.
 *  - Filters in the header (estado of reservas / eventos) re-fetch on change.
 *
 * Data flow:
 *  - On mount / month change → fetches `/api/reservas?desde=&hasta=` and
 *    `/api/eventos?desde=&hasta=` in parallel for the visible 42-day window.
 *  - Items are grouped client-side by `fecha` (YYYY-MM-DD) for O(1) cell lookups.
 *
 * Why a custom calendar (no react-day-picker):
 *  - react-day-picker is great for date-pickers but heavy/styled for a dashboard
 *    grid with embedded business chips. A 70-line custom grid is lighter and
 *    matches the existing CRM aesthetic better.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    CalendarDays,
    Users,
    PartyPopper,
    X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* ---------- date helpers (pure, no external deps) ---------- */

const MONTHS_ES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Format Date → "YYYY-MM-DD" in local time (matches what the API stores). */
function toISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/**
 * Build the 42-cell (6 weeks × 7 days) grid that always starts on the Monday
 * before (or on) the first of the month and ends on the Sunday after (or on)
 * the last day of the month.
 */
function buildMonthGrid(year, month) {
    const first = new Date(year, month, 1);
    // JS getDay(): 0=Sun … 6=Sat. We want Monday-first → shift so Mon=0.
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - offset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        cells.push(d);
    }
    return cells;
}

/* ---------- component ---------- */

export default function CrmCalendario() {
    const navigate = useNavigate();
    const [cursor, setCursor] = useState(() => {
        const t = new Date();
        return { year: t.getFullYear(), month: t.getMonth() };
    });
    const [reservas, setReservas] = useState([]);
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null); // ISO string

    // The 42-day window for the currently displayed month.
    const grid = useMemo(
        () => buildMonthGrid(cursor.year, cursor.month),
        [cursor],
    );
    const rangeStart = toISO(grid[0]);
    const rangeEnd = toISO(grid[grid.length - 1]);

    // Fetch reservas + eventos in parallel whenever the month changes.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            api.get("/reservas", { params: { desde: rangeStart, hasta: rangeEnd } }),
            api.get("/eventos", { params: { desde: rangeStart, hasta: rangeEnd } }),
        ])
            .then(([rRes, eRes]) => {
                if (cancelled) return;
                setReservas(rRes.data || []);
                setEventos(eRes.data || []);
            })
            .catch((err) => !cancelled && toast.error(formatApiError(err)))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [rangeStart, rangeEnd]);

    // Group items by their ISO date so each cell is O(1) lookup.
    const itemsByDay = useMemo(() => {
        const map = {};
        for (const r of reservas) {
            if (!r.fecha) continue;
            (map[r.fecha] = map[r.fecha] || { reservas: [], eventos: [] }).reservas.push(r);
        }
        for (const e of eventos) {
            if (!e.fecha) continue;
            (map[e.fecha] = map[e.fecha] || { reservas: [], eventos: [] }).eventos.push(e);
        }
        return map;
    }, [reservas, eventos]);

    const todayISO = toISO(new Date());

    /** Month navigation: -1 / today / +1 */
    const shift = (delta) => {
        setCursor((c) => {
            const d = new Date(c.year, c.month + delta, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
    };
    const goToday = () => {
        const t = new Date();
        setCursor({ year: t.getFullYear(), month: t.getMonth() });
    };

    const selected = selectedDay ? itemsByDay[selectedDay] : null;

    return (
        <div className="space-y-8">
            {/* Header ----------------------------------------------------- */}
            <header className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                        Calendario
                    </p>
                    <h1 className="font-heading text-4xl md:text-5xl text-crema font-light">
                        {MONTHS_ES[cursor.month]}{" "}
                        <span className="text-dorado italic">{cursor.year}</span>
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => shift(-1)}
                        data-testid="cal-prev"
                        aria-label="Mes anterior"
                        className="w-10 h-10 border border-dorado/30 text-crema hover:border-dorado hover:text-dorado flex items-center justify-center transition-colors"
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <button
                        onClick={goToday}
                        data-testid="cal-today"
                        className="px-4 h-10 border border-dorado/30 text-crema hover:border-dorado hover:text-dorado font-body text-[0.7rem] uppercase tracking-[0.25em] transition-colors"
                    >
                        Hoy
                    </button>
                    <button
                        onClick={() => shift(1)}
                        data-testid="cal-next"
                        aria-label="Mes siguiente"
                        className="w-10 h-10 border border-dorado/30 text-crema hover:border-dorado hover:text-dorado flex items-center justify-center transition-colors"
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
            </header>

            {/* Legend ----------------------------------------------------- */}
            <div className="flex flex-wrap items-center gap-5 text-[0.7rem] uppercase tracking-[0.2em] font-body text-piedra">
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-dorado/80 inline-block" /> Reserva
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-[#7A1F23] inline-block" /> Evento
                </span>
                {loading && <span className="text-piedra italic">Cargando…</span>}
            </div>

            {/* Weekday header --------------------------------------------- */}
            <div className="grid grid-cols-7 gap-px bg-dorado/10 border border-dorado/20">
                {WEEKDAYS_ES.map((w) => (
                    <div
                        key={w}
                        className="bg-carbon-suave/60 py-2 text-center font-body text-[0.65rem] uppercase tracking-[0.25em] text-piedra"
                    >
                        {w}
                    </div>
                ))}
                {/* Day cells ---------------------------------------------- */}
                {grid.map((d) => {
                    const iso = toISO(d);
                    const inMonth = d.getMonth() === cursor.month;
                    const isToday = iso === todayISO;
                    const bucket = itemsByDay[iso];
                    const hasContent = !!bucket;
                    return (
                        <button
                            key={iso}
                            type="button"
                            onClick={() => hasContent && setSelectedDay(iso)}
                            data-testid={`cal-day-${iso}`}
                            className={`relative bg-carbon min-h-[110px] p-2 text-left transition-colors ${
                                inMonth ? "" : "opacity-35"
                            } ${hasContent ? "hover:bg-carbon-suave cursor-pointer" : "cursor-default"} ${
                                isToday ? "ring-1 ring-dorado/70 ring-inset" : ""
                            }`}
                        >
                            <span
                                className={`block font-heading text-sm ${
                                    isToday ? "text-dorado" : "text-crema"
                                }`}
                            >
                                {d.getDate()}
                            </span>
                            {bucket && (
                                <div className="mt-1 space-y-1">
                                    {bucket.reservas.slice(0, 2).map((r) => (
                                        <span
                                            key={r.id}
                                            className="block truncate text-[0.65rem] bg-dorado/15 text-dorado px-1.5 py-0.5 border-l-2 border-dorado"
                                            title={`${r.hora || ""} · ${r.nombre} · ${r.personas} pax`}
                                        >
                                            {r.hora ? `${r.hora} ` : ""}
                                            {r.nombre}
                                        </span>
                                    ))}
                                    {bucket.eventos.slice(0, 2).map((e) => (
                                        <span
                                            key={e.id}
                                            className="block truncate text-[0.65rem] bg-[#7A1F23]/25 text-crema px-1.5 py-0.5 border-l-2 border-[#7A1F23]"
                                            title={`${e.tipo} · ${e.nombre || ""}`}
                                        >
                                            {e.tipo}: {e.nombre || "—"}
                                        </span>
                                    ))}
                                    {(() => {
                                        const total =
                                            bucket.reservas.length + bucket.eventos.length;
                                        const shown =
                                            Math.min(bucket.reservas.length, 2) +
                                            Math.min(bucket.eventos.length, 2);
                                        const extra = total - shown;
                                        return extra > 0 ? (
                                            <span className="block text-[0.6rem] text-piedra italic">
                                                +{extra} más
                                            </span>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Selected-day modal ---------------------------------------- */}
            {selectedDay && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4 md:p-10"
                    onClick={() => setSelectedDay(null)}
                    data-testid="cal-day-modal"
                >
                    <div
                        className="bg-carbon border border-dorado/30 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="flex items-center justify-between px-6 py-4 border-b border-dorado/20">
                            <div>
                                <p className="font-body uppercase tracking-[0.25em] text-[0.65rem] text-dorado">
                                    <CalendarDays size={12} className="inline mb-0.5 mr-1" />
                                    {selectedDay}
                                </p>
                                <h3 className="font-heading text-2xl text-crema mt-1">
                                    Agenda del día
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelectedDay(null)}
                                className="text-piedra hover:text-dorado"
                                aria-label="Cerrar"
                                data-testid="cal-modal-close"
                            >
                                <X size={20} />
                            </button>
                        </header>
                        <div className="p-6 space-y-6">
                            {/* Reservas */}
                            <section>
                                <p className="font-body uppercase tracking-[0.25em] text-[0.65rem] text-dorado mb-3 flex items-center gap-2">
                                    <Users size={12} /> Reservas (
                                    {selected?.reservas.length || 0})
                                </p>
                                {selected?.reservas.length ? (
                                    <ul className="divide-y divide-dorado/10 border border-dorado/15">
                                        {selected.reservas.map((r) => (
                                            <li
                                                key={r.id}
                                                className="px-4 py-3 flex items-center justify-between gap-4"
                                            >
                                                <div>
                                                    <p className="font-heading text-lg text-crema">
                                                        <span className="text-dorado italic mr-2">
                                                            {r.hora || "—"}
                                                        </span>
                                                        {r.nombre}
                                                    </p>
                                                    <p className="font-body text-xs text-piedra">
                                                        {r.personas} pax ·{" "}
                                                        {r.email || r.telefono || "sin contacto"}
                                                    </p>
                                                </div>
                                                <span
                                                    className={`font-body text-[0.6rem] uppercase tracking-[0.2em] px-2 py-1 border ${
                                                        r.estado === "confirmada"
                                                            ? "border-dorado text-dorado"
                                                            : r.estado === "cancelada"
                                                            ? "border-red-400/50 text-red-400"
                                                            : "border-piedra/40 text-piedra"
                                                    }`}
                                                >
                                                    {r.estado}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="font-body text-sm text-piedra italic">
                                        Sin reservas.
                                    </p>
                                )}
                            </section>

                            {/* Eventos */}
                            <section>
                                <p className="font-body uppercase tracking-[0.25em] text-[0.65rem] text-dorado mb-3 flex items-center gap-2">
                                    <PartyPopper size={12} /> Eventos (
                                    {selected?.eventos.length || 0})
                                </p>
                                {selected?.eventos.length ? (
                                    <ul className="divide-y divide-dorado/10 border border-dorado/15">
                                        {selected.eventos.map((e) => (
                                            <li key={e.id}>
                                                <Link
                                                    to={`/crm/eventos/${e.id}`}
                                                    className="block px-4 py-3 hover:bg-carbon-suave/60 transition-colors"
                                                    onClick={() => setSelectedDay(null)}
                                                >
                                                    <p className="font-heading text-lg text-dorado">
                                                        {e.tipo}{" "}
                                                        <span className="text-crema font-light">
                                                            · {e.nombre || "—"}
                                                        </span>
                                                    </p>
                                                    <p className="font-body text-xs text-piedra">
                                                        {e.personas || "—"} pax · {e.estado}
                                                    </p>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="font-body text-sm text-piedra italic">
                                        Sin eventos.
                                    </p>
                                )}
                            </section>

                            <div className="flex flex-wrap gap-3 pt-2 border-t border-dorado/15">
                                <button
                                    onClick={() => {
                                        setSelectedDay(null);
                                        navigate(`/crm/reservas`);
                                    }}
                                    className="px-4 py-2 border border-dorado/40 text-crema hover:border-dorado hover:text-dorado font-body text-[0.7rem] uppercase tracking-[0.25em] transition-colors"
                                >
                                    Ver lista de reservas
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedDay(null);
                                        navigate(`/crm/eventos`);
                                    }}
                                    className="px-4 py-2 border border-dorado/40 text-crema hover:border-dorado hover:text-dorado font-body text-[0.7rem] uppercase tracking-[0.25em] transition-colors"
                                >
                                    Ver lista de eventos
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
